// People and compliance plan: planning rules shared by the API (src/api/routes/peoplePlan.js)
// and the page. Pure functions only; no I/O.
//
// A "who" reference is either a group key below or a people.id.

export const CATEGORIES = [
  'Performance & development', 'Pay & reward', 'Organisation & governance', 'Culture & engagement', 'Certifications',
];
export const TYPES = ['task', 'event', 'audit', 'milestone'];
export const STATUSES = ['Not started', 'In progress', 'Done', 'On hold'];
export const PRIORITIES = ['High', 'Medium', 'Low'];
export const GROUP_KEYS = ['line_managers', 'slt', 'hr', 'middle_managers', 'all_employees'];
export const GROUP_LABELS = {
  line_managers: 'Line managers', slt: 'SLT', hr: 'HR', middle_managers: 'Middle managers', all_employees: 'All employees',
};
export const DEFAULT_CAPACITY = 3;
export const CHANGE_WINDOW_MONTHS = [3, 9]; // March and September each year

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const DATE_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

export function addMonths(ym, n) {
  let y = +ym.slice(0, 4), m = +ym.slice(5, 7) + n;
  y += Math.floor((m - 1) / 12);
  m = ((m - 1) % 12 + 12) % 12 + 1;
  return `${y}-${String(m).padStart(2, '0')}`;
}

export function rollingWindow(today) {
  const from = addMonths(today.slice(0, 7), -1);
  return { from, to: addMonths(from, 14) };
}

export function monthRange(from, to) {
  const out = [];
  for (let m = from; m <= to; m = addMonths(m, 1)) out.push(m);
  return out;
}

export const isChangeWindow = ym => CHANGE_WINDOW_MONTHS.includes(+ym.slice(5, 7));
export const daysInMonth = ym => new Date(Date.UTC(+ym.slice(0, 4), +ym.slice(5, 7), 0)).getUTCDate();
export const dayNumber = d => Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10)) / 86400000;

/** The item's period: first day of its start month (or its deadline month) to its deadline. */
export function period(item) {
  if (!item.deadline) return null;
  return [`${item.start || item.deadline.slice(0, 7)}-01`, item.deadline];
}

/** Share of the item's effort falling in each of `months`, by calendar day. */
export function shareByMonth(item, months) {
  const p = period(item);
  if (!p) return {};
  const a = dayNumber(p[0]), b = dayNumber(p[1]), total = b - a + 1;
  if (total <= 0) return {};
  const out = {};
  for (const m of months) {
    const lo = Math.max(a, dayNumber(`${m}-01`)), hi = Math.min(b, dayNumber(`${m}-${daysInMonth(m)}`));
    if (hi >= lo) out[m] = (hi - lo + 1) / total;
  }
  return out;
}

/** People ids a "who" reference stands for. Groups never include leavers; a named person always counts. */
export function expandWho(who, people) {
  if (who === 'all_employees') return ['__all'];
  if (GROUP_KEYS.includes(who)) return people.filter(p => !p.left && (p.groups || []).includes(who)).map(p => p.id);
  return people.some(p => p.id === who) ? [who] : [];
}

/** Load per person per month, and what makes up each cell. */
export function computeLoad(items, people, months) {
  const L = {}, C = {};
  for (const t of items) {
    const share = shareByMonth(t, months);
    for (const e of t.effort || []) {
      for (const id of expandWho(e.who, people)) {
        for (const [m, f] of Object.entries(share)) {
          const days = (+e.days || 0) * f;
          if (!days) continue;
          (L[id] ||= {})[m] = (L[id][m] || 0) + days;
          (C[`${id}|${m}`] ||= []).push({ item: t, days, est: !!e.est, via: e.who });
        }
      }
    }
  }
  return { L, C };
}

export function netCapacity(p) {
  const cap = +p.capacity > 0 ? +p.capacity : DEFAULT_CAPACITY;
  return Math.max(0, cap - (+p.standing_days || 0));
}

const refsOf = t => [t.owner, ...(t.involved || []), ...(t.signoff || []), ...(t.effort || []).map(e => e.who)].filter(Boolean);

export function itemPeople(item, people) {
  return new Set(refsOf(item).flatMap(w => expandWho(w, people)));
}

/** People worth a row: in a group, named on an item, or with a capacity set. */
export function planPeople(items, people) {
  const named = new Set(items.flatMap(refsOf));
  return people.filter(p =>
    named.has(p.id) || p.capacity != null || (!p.left && (p.groups || []).some(g => g !== 'all_employees')));
}

const str = v => (v == null ? '' : String(v)).trim();
const list = v => (Array.isArray(v) ? v.map(str).filter(Boolean) : []);

/** Validate and clean an item from a request or a form. Returns { item } or { error }. */
export function normaliseItem(b = {}) {
  const title = str(b.title);
  if (!title) return { error: 'Add a title' };
  const deadline = str(b.deadline);
  if (!DATE_RE.test(deadline)) return { error: "Add a valid 'must be done by' date" };
  const start = str(b.start);
  if (start && (!MONTH_RE.test(start) || start > deadline.slice(0, 7))) return { error: 'The start month must be on or before the deadline' };
  const category = str(b.category) || CATEGORIES[0];
  if (!CATEGORIES.includes(category)) return { error: 'Choose a category' };
  const pick = (v, opts) => (opts.includes(str(v)) ? str(v) : opts[0]);
  return {
    item: {
      title, deadline, start, category,
      type: pick(b.type, TYPES),
      status: pick(b.status, STATUSES),
      priority: pick(b.priority, ['Medium', ...PRIORITIES]),
      visibility: b.visibility === 'slt' ? 'slt' : 'company',
      contractors: !!b.contractors,
      owner: str(b.owner),
      involved: list(b.involved),
      signoff: list(b.signoff),
      depends: list(b.depends),
      effort: (Array.isArray(b.effort) ? b.effort : [])
        .map(e => ({ who: str(e && e.who), days: Math.max(0, +(e && e.days) || 0), est: !!(e && e.est) }))
        .filter(e => e.who),
      milestones: (Array.isArray(b.milestones) ? b.milestones : [])
        .map(x => ({ label: str(x && x.label), date: str(x && x.date), done: !!(x && x.done) }))
        .filter(x => x.label || x.date)
        .map(x => ({ ...x, date: DATE_RE.test(x.date) ? x.date : '' })),
      notes: str(b.notes), process: str(b.process), questions: str(b.questions), assumptions: str(b.assumptions),
    },
  };
}
